import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, X, CheckCircle, Mic, MicOff, Send, Loader2, Bookmark, ShieldAlert, Heart, Phone } from 'lucide-react';
import { detectHarmContent, detectUnconstructiveContent, containsProfanity, VoiceInputButton, FIREBASE_CONFIG } from '../shared.jsx';
import AiContentReportButton from './AiContentReportButton.jsx';
import ModalDialog from './ModalDialog.jsx';

// ═══════════════════════════════════════════════════════════════
//  SAY IT YOUR WAY — Panel Component
//  Embeddable slide-out chat panel for guided conflict entry.
//  Props: isOpen, onClose, onExtracted(data), userId
//
//  NOTE: The system prompt lives SERVER-SIDE in the Cloud Function.
//  This prevents users from inspecting/tampering with it.
// ═══════════════════════════════════════════════════════════════

// ⚠️ Gen 2 Cloud Function URL — update after deploying if the URL changes.
const CLOUD_FUNCTION_URL = 'https://us-central1-argumentor-ff17a.cloudfunctions.net/sayItYourWayChat';

const GREETING = "Hey there! 👋 I'm here to help you get things sorted out. Just tell me what's going on in your own words. No need to be organized about it. I'll help make sense of it all.\n\nSo, what's been happening?";

// Draft expiry: drafts older than 5 minutes are stale and should not restore
const DRAFT_MAX_AGE_MS = 5 * 60 * 1000;

// ── Helpers ──────────────────────────────────────────────────────

const analyzeEmotionalTone = (messages) => {
  if (!messages.length) return { level: 0, label: "Neutral", color: "stone" };
  const userMessages = messages.filter((m) => m.role === "user");
  if (!userMessages.length) return { level: 0, label: "Neutral", color: "stone" };

  const recentMessages = userMessages.slice(-3);
  const text = recentMessages.map((m) => m.content).join(" ").toLowerCase();

  const highIntensity = [/!{2,}/, /\b(hate|furious|livid|enraged|disgusting|unbearable|worst|terrible|horrible|outrageous)\b/i, /\b(can't stand|sick of|fed up|had enough|last straw|done with)\b/i, /\b(always|never|every single time|constantly)\b/i, /[A-Z]{3,}/];
  const medIntensity = [/\b(angry|upset|frustrated|annoyed|irritated|bothered|stressed|anxious|worried|hurt|disappointed)\b/i, /\b(unfair|wrong|shouldn't|problem|issue|conflict|argument|fight|disagree)\b/i, /!$/];
  const lowIntensity = [/\b(think|feel|wonder|hope|wish|prefer|understand|see their point|maybe|perhaps)\b/i, /\b(want to resolve|work through|figure out|compromise|solution|better)\b/i];

  let score = 50;
  highIntensity.forEach((r) => { if (r.test(text)) score += 15; });
  medIntensity.forEach((r) => { if (r.test(text)) score += 8; });
  lowIntensity.forEach((r) => { if (r.test(text)) score -= 10; });

  if (userMessages.length > 2) {
    const early = userMessages.slice(0, 2).map((m) => m.content).join(" ");
    const late = userMessages.slice(-2).map((m) => m.content).join(" ");
    if ((late.match(/!/g) || []).length < (early.match(/!/g) || []).length) score -= 10;
    if ((late.match(/[A-Z]{3,}/g) || []).length < (early.match(/[A-Z]{3,}/g) || []).length) score -= 10;
  }

  score = Math.max(0, Math.min(100, score));
  if (score >= 75) return { level: score, label: "Very Heated", color: "red" };
  if (score >= 55) return { level: score, label: "Frustrated", color: "orange" };
  if (score >= 35) return { level: score, label: "Processing", color: "amber" };
  if (score >= 15) return { level: score, label: "Calming Down", color: "emerald" };
  return { level: score, label: "Composed", color: "green" };
};

const extractJSON = (text) => {
  const match = text.match(/```json\s*([\s\S]*?)\s*```/);
  if (match) { try { return JSON.parse(match[1]); } catch (e) { return null; } }
  return null;
};

const cleanDisplayText = (text) => text.replace(/```json[\s\S]*?```/, "").trim();

// Client-side output sanitization — defense-in-depth with server-side
const CLIENT_OUTPUT_PROFANITY = [
  'fuck', 'shit', 'damn', 'bitch', 'asshole', 'bastard', 'crap', 'piss', 'dick',
  'cock', 'pussy', 'slut', 'whore', 'cunt', 'twat', 'prick', 'dumbass', 'jackass',
  'bullshit', 'shitty', 'fucking', 'bitching', 'shite', 'bellend', 'tosser', 'pillock'
];
const CLIENT_OUTPUT_REPLACEMENTS = [
  [/\b(idiot|moron|loser|jerk|scumbag|trash|worthless)\b/gi, 'the other party'],
  [/\b(narcissist(ic)?|sociopath(ic)?|psycho(path|pathic)?)\b/gi, 'person whose behavior feels harmful'],
  [/\b(gasligh(t|ting|ted|ter))\b/gi, "dismissing the other's perspective"],
  [/\b(manipulat(ive|or|ing)|control\s*freak)\b/gi, 'controlling behavior'],
  [/\btoxic\b/gi, 'harmful'],
  [/\b(drunk(ard)?|alcoholic|lush|boozer)\b/gi, 'heavy drinking'],
  [/\b(junkie|druggie|pothead|stoner|crackhead)\b/gi, 'substance use'],
  [/\b(lost\s+(their|his|her)\s+(mind|shit|marbles))\b/gi, 'became very upset'],
  [/\b(went\s+(ballistic|berserk|postal|nuts|crazy))\b/gi, 'reacted with strong emotion'],
  [/\b(flip(s|ped|ping)?\s+out)\b/gi, 'reacted strongly'],
  [/\b(had\s+a\s+meltdown|melt(s|ing)?\s*down)\b/gi, 'became very overwhelmed'],
  [/\b(threw?\s+a\s+(fit|tantrum))\b/gi, 'had a strong emotional reaction'],
  [/\b(drama\s+queen|cry\s*baby|snowflake)\b/gi, 'someone who is upset'],
  [/\b(act(s|ing)?\s+like\s+a\s+(child|baby|brat))\b/gi, 'behaving immaturely'],
  [/\b(playing\s+the\s+victim)\b/gi, 'deflecting responsibility'],
  [/\b(overreact(s|ing|ed)?)\b/gi, 'reacted strongly'],
  [/\b(always\s+ruins?|ruined?\s+everything)\b/gi, 'caused significant problems'],
];
const sanitizeExtractedField = (text) => {
  if (!text || typeof text !== 'string') return text;
  let cleaned = text;
  for (const word of CLIENT_OUTPUT_PROFANITY) {
    cleaned = cleaned.replace(new RegExp('\\b' + word + '\\b', 'gi'), '');
  }
  for (const [pattern, replacement] of CLIENT_OUTPUT_REPLACEMENTS) {
    cleaned = cleaned.replace(pattern, replacement);
  }
  return cleaned.replace(/\s{2,}/g, ' ').trim();
};
const sanitizeExtractedData = (data) => {
  if (!data) return data;
  const sanitized = { ...data };
  for (const field of ['who', 'what', 'when', 'where', 'why', 'how', 'desiredOutcome', 'summary', 'title']) {
    if (sanitized[field]) sanitized[field] = sanitizeExtractedField(sanitized[field]);
  }
  return sanitized;
};

// Client-side harm detection
const HARM_PATTERNS = {
  selfHarm: [
    /\b(want(?:s|ing)?\s+to\s+(?:die|end\s+(?:it|my\s+life|everything)|kill\s+myself|not\s+(?:be\s+here|exist|live|wake\s+up)))\b/i,
    /\b(suicid(?:e|al)|self[- ]?harm|cut(?:ting)?\s+my(?:self)?|hurt(?:ing)?\s+myself)\b/i,
    /\b(no\s+(?:reason|point)\s+(?:to|in)\s+(?:live|living|go(?:ing)?\s+on|continue|continuing))\b/i,
    /\b(better\s+off\s+(?:dead|without\s+me|if\s+i\s+(?:wasn't|weren't)\s+here))\b/i,
    /\b(can'?t\s+(?:take|do)\s+(?:it|this)\s+anymore|end(?:ing)?\s+(?:it\s+all|the\s+pain|my\s+suffering))\b/i,
    /\b(goodbye\s+(?:cruel\s+)?world|final\s+(?:goodbye|note|letter))\b/i,
  ],
  harmToOthers: [
    /\b((?:going\s+to|gonna|want\s+to|plan(?:ning)?\s+to)\s+(?:kill|hurt|attack|stab|shoot|poison|strangle|murder))\b/i,
    /\b((?:they|he|she)\s+(?:deserve(?:s)?\s+to\s+die|should\s+(?:die|be\s+killed|be\s+dead)))\b/i,
    /\b(bring(?:ing)?\s+(?:a\s+)?(?:gun|knife|weapon))\b/i,
    /\b((?:make|watch)\s+(?:them|him|her)\s+(?:pay|suffer|bleed|hurt))\b/i,
  ],
};

const detectHarmClientSide = (text) => {
  if (!text || text.trim().length < 5) return { detected: false, type: null };
  const cleaned = text.replace(/[*_~`#]/g, '').trim();
  let selfHarm = false, harmOthers = false;
  for (const p of HARM_PATTERNS.selfHarm) { if (p.test(cleaned)) { selfHarm = true; break; } }
  for (const p of HARM_PATTERNS.harmToOthers) { if (p.test(cleaned)) { harmOthers = true; break; } }
  if (!selfHarm && !harmOthers) return { detected: false, type: null };
  return { detected: true, type: selfHarm && harmOthers ? 'both' : selfHarm ? 'self' : 'others' };
};

const sanitizeInput = (text) => {
  let cleaned = text;
  cleaned = cleaned.replace(/(?:ignore|forget|disregard|override)\s+(?:all\s+)?(?:previous|prior|above|your)\s+(?:instructions|rules|prompt|guidelines)/gi, '[removed]');
  cleaned = cleaned.replace(/(?:you\s+are\s+now|pretend\s+(?:you\s+are|to\s+be)|act\s+as\s+(?:if\s+you\s+are|a))\s/gi, '[removed] ');
  cleaned = cleaned.replace(/(?:system|developer|admin)\s*(?:prompt|message|override|mode)\s*[:=]/gi, '[removed]:');
  cleaned = cleaned.replace(/```(?:system|prompt|instruction)[\s\S]*?```/gi, '[removed]');
  return cleaned;
};

// ── Draft helpers (outside component to avoid closure issues) ──
const DRAFT_KEY = "sayityourway-draft";

function saveDraftToStorage(messages) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({
      messages, timestamp: Date.now()
    }));
  } catch (e) {}
}

function loadDraftFromStorage() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    // Don't load stale drafts (older than 5 min)
    if (!draft.timestamp || Date.now() - draft.timestamp > DRAFT_MAX_AGE_MS) {
      localStorage.removeItem(DRAFT_KEY);
      return null;
    }
    if (draft.messages?.length) return draft.messages;
  } catch (e) {}
  return null;
}

function clearDraftFromStorage() {
  try { localStorage.removeItem(DRAFT_KEY); } catch (e) {}
}

// ── Panel Component ─────────────────────────────────────────────
export default function SayItYourWayPanel({ isOpen, onClose, onExtracted }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [crisisDetected, setCrisisDetected] = useState(null);
  const [emotionalTone, setEmotionalTone] = useState({ level: 0, label: "Neutral", color: "stone" });
  const [draftSaved, setDraftSaved] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const extractedRef = useRef(false);

  // Scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Update emotional tone
  useEffect(() => {
    setEmotionalTone(analyzeEmotionalTone(messages));
  }, [messages]);

  // Auto-save draft every 30 seconds (only if not extracted)
  useEffect(() => {
    if (messages.length === 0) return;
    const interval = setInterval(() => {
      if (!extractedRef.current) saveDraftToStorage(messages);
    }, 30000);
    return () => clearInterval(interval);
  }, [messages]);

  // ── CORE RESET LOGIC ──────────────────────────────────────────
  // Every time isOpen becomes true, start completely fresh.
  // The only exception: if there's a recent draft (< 5 min old),
  // restore it on the very first open.
  useEffect(() => {
    if (!isOpen) return;

    // Reset all state to pristine
    extractedRef.current = false;
    setExtractedData(null);
    setError(null);
    setInput("");
    setDraftLoaded(false);
    setCrisisDetected(null);
    setEmotionalTone({ level: 0, label: "Neutral", color: "stone" });

    // Try loading a recent draft
    const draftMessages = loadDraftFromStorage();
    if (draftMessages) {
      setMessages(draftMessages);
      setDraftLoaded(true);
    } else {
      setMessages([{ role: "assistant", content: GREETING }]);
    }

    // Always clear draft on open — if it was good, we already loaded it
    clearDraftFromStorage();
  }, [isOpen]);

  // Notify parent when data is extracted
  useEffect(() => {
    if (extractedData && onExtracted) {
      extractedRef.current = true;
      onExtracted(extractedData);
      clearDraftFromStorage();
    }
  }, [extractedData]);

  // ── Voice Input ───────────────────────────────────────────────
  const toggleVoiceInput = () => {
    if (isRecording) { recognitionRef.current?.stop(); setIsRecording(false); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setError("Voice input not supported in this browser."); return; }
    const recognition = new SR();
    recognition.continuous = false; recognition.interimResults = false; recognition.lang = "en-US";
    recognition.onresult = (e) => { setInput((prev) => prev + (prev ? " " : "") + e.results[0][0].transcript); setIsRecording(false); };
    recognition.onerror = () => setIsRecording(false);
    recognition.onend = () => setIsRecording(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  };

  // ── Send Message ──────────────────────────────────────────────
  const sendMessage = async (userMessage) => {
    if (!userMessage.trim()) return;
    const sanitized = sanitizeInput(userMessage);

    // Client-side harm detection
    const harmCheck = detectHarmClientSide(sanitized);
    if (harmCheck.detected) {
      setCrisisDetected(harmCheck.type);
      setMessages((prev) => [...prev,
        { role: "user", content: sanitized },
        { role: "assistant", content: harmCheck.type === 'self' || harmCheck.type === 'both'
          ? "I want to make sure you're okay. If you're having thoughts of hurting yourself, please reach out for support right now:\n\n📞 988 Suicide & Crisis Lifeline: Call or text 988 (24/7, free & confidential)\n💬 Crisis Text Line: Text HELLO to 741741\n🚨 Emergency: Call 911\n\nYou matter, and help is available. When you're ready to continue describing your conflict, I'm here."
          : "I'm not able to continue this conversation as it sounds like someone may be at risk of harm. If there is an immediate danger, please contact 911.\n\nIf you'd like to describe a conflict for peaceful resolution, I'm here for that."
        }
      ]);
      setInput("");
      inputRef.current?.focus();
      return;
    }

    const newMessages = [...messages, { role: "user", content: sanitized }];
    setMessages(newMessages);
    setInput("");
    inputRef.current?.focus();
    setIsLoading(true);
    setError(null);

    try {
      const token = await window.firebaseAuth?.currentUser?.getIdToken(false);
      const response = await fetch(CLOUD_FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          messages: newMessages.map((m) => ({ role: m.role, content: m.content })),
        }),
      });

      if (response.status === 429) {
        setError("You're sending messages too quickly. Please wait a moment.");
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Server error (${response.status})`);
      }

      const data = await response.json();
      const assistantText = data.content?.map((c) => c.text || "").join("") || "I had trouble understanding. Could you try again?";

      const rawExtracted = extractJSON(assistantText);
      if (rawExtracted?.extracted) {
        const extracted = sanitizeExtractedData(rawExtracted);
        setExtractedData(extracted);
      }

      setMessages([...newMessages, { role: "assistant", content: assistantText }]);
    } catch (err) {
      setError(err.message || "Failed to get a response. Please try again.");
      console.error("API error:", err);
    }
    setIsLoading(false);
  };

  // Manual save draft
  const handleSaveDraft = () => {
    if (extractedRef.current) return;
    saveDraftToStorage(messages);
    setDraftSaved(true);
    setTimeout(() => setDraftSaved(false), 2000);
  };

  // ── Tone Colors ───────────────────────────────────────────────
  const toneColors = {
    red: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", fill: "bg-red-500" },
    orange: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700", fill: "bg-orange-500" },
    amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", fill: "bg-amber-500" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", fill: "bg-emerald-500" },
    green: { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", fill: "bg-green-500" },
    stone: { bg: "bg-stone-50", border: "border-stone-200", text: "text-stone-600", fill: "bg-stone-400" },
  };
  const tc = toneColors[emotionalTone.color] || toneColors.stone;

  if (!isOpen) return null;

  // ── Render ────────────────────────────────────────────────────
  return (
    <>
      <div className="fixed top-0 right-0 h-full bg-white shadow-2xl border-l border-stone-200 z-50 transition-all duration-500 ease-in-out flex flex-col w-full md:w-[440px]">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-base">Say It Your Way</h2>
              <p className="text-purple-50 text-xs">Tell us what happened, and we'll organize it</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {draftSaved && (
              <span className="text-[10px] bg-white/20 text-white px-2 py-0.5 rounded-full animate-pulse">Draft saved</span>
            )}
            <button onClick={handleSaveDraft} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" title="Save draft" aria-label="Save draft">
              <Bookmark className="w-4 h-4 text-white" />
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" aria-label="Close">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Emotional Tone Indicator */}
        {messages.filter((m) => m.role === "user").length > 0 && (
          <div className={`px-5 py-2.5 ${tc.bg} border-b ${tc.border} flex items-center gap-3`}>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className={`text-[11px] font-semibold ${tc.text} uppercase tracking-wider`}>
                  Emotional Tone: {emotionalTone.label}
                </span>
                <span className={`text-[10px] ${tc.text}`}>{emotionalTone.level}%</span>
              </div>
              <div className="h-1.5 bg-white/60 rounded-full overflow-hidden">
                <div className={`h-full ${tc.fill} rounded-full transition-all duration-1000 ease-out`} style={{ width: `${emotionalTone.level}%` }} />
              </div>
            </div>
            {emotionalTone.level <= 35 && messages.filter((m) => m.role === "user").length > 2 && (
              <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">🌿 Getting calmer</span>
            )}
          </div>
        )}

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {draftLoaded && (
            <div className="text-center">
              <span className="text-xs bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-medium">📋 Draft restored from your last session</span>
            </div>
          )}

          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                msg.role === "user" ? "bg-purple-600 text-white rounded-br-md" : "bg-stone-100 text-stone-800 rounded-bl-md"
              }`}>
                {msg.role === "assistant" ? cleanDisplayText(msg.content) : msg.content}
                {msg.role === "assistant" && (
                  <div className="mt-1 flex justify-end">
                    <AiContentReportButton
                      content={msg.content}
                      context={messages.slice(Math.max(0, idx - 2), idx)}
                      source="siyw-create-conflict"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-stone-100 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="text-center">
              <span className="text-xs text-red-700 bg-red-50 px-3 py-1.5 rounded-full">⚠️ {error}</span>
            </div>
          )}

          {/* Summary Confirmation Card */}
          {extractedData && (
            <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-2 border-green-200">
              <p className="text-sm font-semibold text-green-800 mb-2">✅ Got it! I've filled in the form fields for you.</p>
              <p className="text-xs text-green-700 mb-3">
                Check the form on the left. Everything is pre-filled. You can edit anything directly, or tell me what needs correcting.
              </p>
              <div className="space-y-1.5 mb-3">
                {Object.entries({
                  "Title": extractedData.title, "Who": extractedData.who, "What": extractedData.what,
                  "When": extractedData.when, "Where": extractedData.where, "Why": extractedData.why,
                  "Outcome": extractedData.desiredOutcome,
                }).map(([label, value]) =>
                  value ? (
                    <div key={label} className="flex items-start gap-2 text-xs">
                      <span className="font-semibold text-green-800 w-14 flex-shrink-0">{label}:</span>
                      <span className="text-green-700">{value}</span>
                    </div>
                  ) : null
                )}
              </div>
              <p className="text-xs text-green-700 italic">
                Need to change something? Just tell me, like "Actually, this happened on Tuesday" or "Add my manager to the who."
              </p>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Chat Input */}
        <div className="border-t border-stone-200 px-4 py-3 bg-white flex-shrink-0">
          <div className="flex items-end gap-2">
            <button
              onClick={toggleVoiceInput}
              className={`p-2.5 rounded-xl transition-all flex-shrink-0 ${
                isRecording ? "bg-red-600 text-white shadow-lg shadow-red-200 animate-pulse" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
              }`}
              title={isRecording ? "Stop recording" : "Voice input"}
            >
              {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>

            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
              className="flex-1 px-4 py-2.5 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
              placeholder={isRecording ? "🎙️ Listening..." : "Type what's on your mind..."}
              rows={1}
              disabled={isLoading}
              style={{ minHeight: "42px", maxHeight: "120px" }}
            />

            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || isLoading}
              className="p-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-stone-300 text-white rounded-xl transition-all flex-shrink-0 active:scale-95"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-stone-500">Press Enter to send · Shift+Enter for new line</span>
            {isRecording && <span className="text-[10px] text-red-700 font-medium animate-pulse">● Recording...</span>}
          </div>
          <div className="mt-2 pt-2 border-t border-stone-100">
            <p className="text-[9px] text-stone-500 leading-snug text-center">
              This tool helps organize your conflict for resolution. It is not therapy, counseling, or legal advice.
              If you're in crisis, call <a href="tel:988" className="underline">988</a> or <a href="tel:911" className="underline">911</a>.
            </p>
          </div>
        </div>
      </div>

      {/* Crisis Resource Modal */}
      {crisisDetected && (
        <ModalDialog onClose={() => setCrisisDetected(null)} labelledBy="siyw-panel-crisis-title" className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4" onClick={() => setCrisisDetected(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-red-600 to-rose-600 rounded-t-2xl px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 id="siyw-panel-crisis-title" className="text-xl font-bold text-white">We Care About You</h2>
                  <p className="text-white text-sm">You are not alone. Help is available.</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {(crisisDetected === 'self' || crisisDetected === 'both') && (
                <div className="space-y-3">
                  <p className="text-stone-700 text-sm leading-relaxed">
                    We noticed some language that may indicate you're going through a very difficult time. Your safety matters.
                  </p>
                  <a href="tel:988" className="flex items-center gap-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl hover:bg-red-100 transition-colors">
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-red-800 text-lg">988 Suicide & Crisis Lifeline</p>
                      <p className="text-red-700 text-sm">Call or text <span className="font-bold">988</span> (24/7, free & confidential)</p>
                    </div>
                  </a>
                  <a href="sms:741741&body=HELLO" className="flex items-center gap-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl hover:bg-blue-100 transition-colors">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-blue-800 text-lg">Crisis Text Line</p>
                      <p className="text-blue-600 text-sm">Text <span className="font-bold">HELLO</span> to <span className="font-bold">741741</span></p>
                    </div>
                  </a>
                </div>
              )}
              {(crisisDetected === 'others' || crisisDetected === 'both') && (
                <div className="space-y-3">
                  <p className="text-stone-700 text-sm">If there is an immediate emergency, please contact your local authorities.</p>
                  <a href="tel:911" className="flex items-center gap-4 p-4 bg-red-50 border-2 border-red-200 rounded-xl hover:bg-red-100 transition-colors">
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-red-800 text-lg">Emergency: 911</p>
                      <p className="text-red-700 text-sm">Call if anyone is in immediate danger</p>
                    </div>
                  </a>
                </div>
              )}
              <button onClick={() => setCrisisDetected(null)} className="w-full py-3 bg-stone-800 hover:bg-stone-900 text-white rounded-xl font-medium transition-colors">
                I Understand
              </button>
            </div>
          </div>
        </ModalDialog>
      )}
    </>
  );
}
